/**
 * Parsing and formatting for the two multi-value affiliate_clean fields the edit
 * form now offers pickers for: vertical, and the per-country traffic block.
 *
 * Both are comma-joined text columns, so the exact shape written back matters —
 * top_geos_array is a GENERATED column derived from top_geos, and other pages
 * filter on it. These helpers keep the format identical to what the SEMrush
 * merge (TriggerMerge.js) writes:
 *
 *   vertical                 "Casino,Sports Betting"
 *   top_geos                 "Germany, Spain"
 *   top_geo_organic_traffic  "Germany: 5000, Spain: 1200"
 *   top_geo_adwords_traffic  "Germany: 300, Spain: 90"
 */

// Offered by the picker. Mirrors VERTICAL_OPTIONS in the backend controller.
export const VERTICAL_OPTIONS = [
  'Casino',
  'Sports Betting',
  'Bingo',
  'Poker',
  'Lottery',
  'Sweepstake',
  'Crypto'
];

// The column is free text and ~15 years of imports have left several spellings
// of the same thing (CASINO, SPORTS_BETTING, Sweepstakes, Sportsbetting). They
// all mean one of the seven above, so they map onto it rather than showing up
// as unrecognised.
const VERTICAL_ALIASES = {
  casino: 'Casino',
  'sports betting': 'Sports Betting',
  sportsbetting: 'Sports Betting',
  sportbetting: 'Sports Betting',
  sports: 'Sports Betting',
  betting: 'Sports Betting',
  bingo: 'Bingo',
  poker: 'Poker',
  lottery: 'Lottery',
  sweepstake: 'Sweepstake',
  sweepstakes: 'Sweepstake',
  crypto: 'Crypto'
};

const splitCsv = (value) =>
  !value ? [] : String(value).split(',').map((v) => v.trim()).filter(Boolean);

/** "SPORTS_BETTING" -> "Sports Betting"; unknown tokens come back null. */
export const canonicalVertical = (token) => {
  const key = String(token || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return VERTICAL_ALIASES[key] || null;
};

/**
 * Splits a stored vertical string into the canonical values the picker knows
 * and anything it does not recognise ("Slots", "1", "Mixed").
 *
 * Unrecognised tokens are returned rather than dropped: the operator opened this
 * modal to change one field, and silently rewriting a column they never touched
 * would lose data.
 */
export const parseVerticals = (value) => {
  const selected = [];
  const custom = [];

  for (const token of splitCsv(value)) {
    const canonical = canonicalVertical(token);
    if (canonical) {
      if (!selected.includes(canonical)) selected.push(canonical);
    } else if (!custom.includes(token)) {
      custom.push(token);
    }
  }

  return { selected, custom };
};

/** Canonical order first (so the string is stable), then anything preserved. */
export const formatVerticals = (selected, custom = []) => {
  const ordered = VERTICAL_OPTIONS.filter((v) => selected.includes(v));
  return [...ordered, ...custom].join(',');
};

const toCount = (raw) => {
  if (raw === null || raw === undefined) return '';
  const digits = String(raw).replace(/[^0-9]/g, '');
  return digits === '' ? '' : String(parseInt(digits, 10));
};

/**
 * "Germany: 5000, Spain: 1200" -> Map { germany -> { name: 'Germany', count: '5000' } }
 *
 * Keyed lowercase so the three columns line up regardless of casing, but the
 * name is carried through as written — it is what gets saved back.
 */
const parseGeoValues = (value) => {
  const map = new Map();
  for (const token of splitCsv(value)) {
    const idx = token.indexOf(':');
    if (idx === -1) continue;
    const name = token.slice(0, idx).trim();
    const count = toCount(token.slice(idx + 1));
    if (name && count !== '') map.set(name.toLowerCase(), { name, count });
  }
  return map;
};

/**
 * Builds the editable rows from the three stored columns.
 *
 * top_geos is normally bare names ("Germany, Spain") but a chunk of older rows
 * store "Germany:64347274,..." in it instead, so the country name is taken as
 * the part before the first colon either way, and a number found there is used
 * when the organic column has nothing for that country.
 */
export const parseGeoRows = (topGeos, organicValue, paidValue) => {
  const organic = parseGeoValues(organicValue);
  const paid = parseGeoValues(paidValue);

  const rows = [];
  const seen = new Set();

  for (const token of splitCsv(topGeos)) {
    const idx = token.indexOf(':');
    const country = (idx === -1 ? token : token.slice(0, idx)).trim();
    if (!country) continue;

    const key = country.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    const inlineCount = idx === -1 ? '' : toCount(token.slice(idx + 1));

    rows.push({
      country,
      organic: organic.get(key)?.count ?? inlineCount,
      paid: paid.get(key)?.count ?? ''
    });
  }

  // A country with traffic recorded but missing from top_geos would otherwise be
  // wiped on the next save.
  for (const [key, entry] of organic) {
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ country: entry.name, organic: entry.count, paid: paid.get(key)?.count ?? '' });
  }

  return rows;
};

/** Rows -> the three columns, in TriggerMerge's format. */
export const formatGeoRows = (rows) => {
  const clean = rows.filter((r) => r.country && r.country.trim());

  const join = (field) =>
    clean
      .filter((r) => toCount(r[field]) !== '')
      .map((r) => `${r.country.trim()}: ${toCount(r[field])}`)
      .join(', ');

  return {
    top_geos: clean.map((r) => r.country.trim()).join(', '),
    top_geo_organic_traffic: join('organic'),
    top_geo_adwords_traffic: join('paid')
  };
};

export const sumGeo = (rows, field) =>
  rows.reduce((total, row) => {
    const count = toCount(row[field]);
    return count === '' ? total : total + parseInt(count, 10);
  }, 0);
